import React, { useCallback, useEffect } from "react";
import { Box, CurrencyExchangeConfirmation } from "@ui-kit/components/index.es";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { compose } from "redux";
import { arrayOf, bool, func, shape, string } from "prop-types";
import { push } from "react-router-redux";
import * as i18n from "util/i18n";
import isTokenActive from "util/token";
import { getAccountDescription } from "util/accounts";
import Yup from "yup";
import { resizableRoute } from "pages/_components/Resizable";
import { useEmbeddedV3ShellLayoutSync } from "util/v3ShellLayoutSync";
import Head from "../../../_components/Head";
import Notification from "../../../_components/Notification";
import {
    actions as currencyExchangeActions,
    selectors as currencyExchangeSelectors,
} from "../../../../reducers/currencyExchange";
import { actions as accountsActions, selectors as accountsSelectors } from "../../../../reducers/accounts";
import { actions as tokenActions, selectors as tokenSelectors } from "../../../../reducers/token";
import { selectors as sessionSelectors } from "../../../../reducers/session";
import { getLabel } from "./currencyExchangeUtils";

function CurrencyExchangeConfirmPage({
    dispatch,
    previewData,
    fetchingConfirm,
    accounts,
    userId,
    tokenNumber,
    fetchingToken,
    isDesktop,
    isRetailEnvironment,
    embeddedInV3Shell,
}) {
    useEffect(() => {
        if (accounts.length === 0) {
            dispatch(accountsActions.listAccounts(userId));
        }
        if (!isDesktop && isTokenActive()) {
            dispatch(tokenActions.softTokenGenerationRequest());
        }
    }, [dispatch, userId, accounts.length, isDesktop]);

    let shellTitleKey = "";
    if (previewData) {
        shellTitleKey =
            previewData.tipoOperacion === "COMPRA" ? "currencyExchange.buy.title" : "currencyExchange.sell.title";
    }

    useEmbeddedV3ShellLayoutSync(dispatch, {
        embeddedInV3Shell,
        isDesktop,
        titleKey: shellTitleKey,
        goBackPath: "/v2/investment/currencyExchange",
        scopes: ["currencyExchange"],
    });

    const handleBack = useCallback(() => {
        dispatch(push("/v2/investment/currencyExchange"));
    }, [dispatch]);

    const otpSchema = Yup.object({
        otp:
            !isDesktop && isTokenActive() && tokenNumber
                ? Yup.string().nullable()
                : Yup.string().required(i18n.get("currencyExchange.confirm.otp.required")),
    });

    const handleConfirm = useCallback(
        (otp) => {
            if (!previewData) {
                return;
            }
            const finalOtp = !isDesktop && isTokenActive() && tokenNumber ? tokenNumber : otp;
            dispatch(
                currencyExchangeActions.confirmExchange(
                    previewData.solicitudCambioId,
                    previewData.tipoOperacion,
                    true,
                    finalOtp,
                ),
            );
        },
        [dispatch, previewData, tokenNumber, isDesktop],
    );

    const handleDownloadDeclaration = useCallback(() => {
        const declarationI18nKey = isRetailEnvironment
            ? "foreign.currency.retail.text"
            : "foreign.currency.corporate.text";
        dispatch(currencyExchangeActions.downloadDeclaration("pdf", declarationI18nKey));
    }, [dispatch, isRetailEnvironment]);

    if (!previewData) {
        dispatch(push("/v2/investment/currencyExchange"));
        return null;
    }

    const isBuyMode = previewData.tipoOperacion === "COMPRA";

    const getFormattedAccount = (accountNumber) => {
        const account = accounts.find((acc) => acc.number === accountNumber);
        if (!account) {
            return accountNumber;
        }
        return getAccountDescription(account);
    };

    const confirmationData = {
        debitAccount: getFormattedAccount(previewData.cuentaDebito),
        creditAccount: getFormattedAccount(previewData.cuentaCredito),
        debitAmount: previewData.importeDebito || 0,
        debitCurrency: isBuyMode ? "ARS" : "USD",
        creditAmount: previewData.importeCredito || 0,
        creditCurrency: isBuyMode ? "USD" : "ARS",
        exchangeRate: previewData.cotizacion || 0,
    };

    const isMobileWithToken = !isDesktop && isTokenActive();

    const getToken = async () => {
        if (isMobileWithToken && tokenNumber) {
            return { token: tokenNumber };
        }
        return { token: null };
    };

    const retailDeclarationContent = i18n.get("foreign.currency.retail.text");
    const corporateDeclarationContent = i18n.get("foreign.currency.corporate.text");

    const isTokenPending = isMobileWithToken && fetchingToken;

    return (
        <>
            {!embeddedInV3Shell && <Notification scopeToShow="currencyExchange" />}
            <Box css={{ backgroundColor: "#f9f9f9" }}>
                <Box className="admin-detail-head" px={isDesktop ? 40 : 24}>
                    {isDesktop && <Head onBack={handleBack} />}
                    {!(embeddedInV3Shell && !isDesktop) && (
                        <Head
                            title={isBuyMode ? "currencyExchange.buy.title" : "currencyExchange.sell.title"}
                            onBack={!isDesktop && handleBack}
                            titleClassName={
                                !isDesktop
                                    ? "header-mobile-title-background-blue overflow-visible"
                                    : "my-3 align-center"
                            }
                            headerClassName={!isDesktop ? "blue-main-header-mobile" : ""}
                        />
                    )}
                    {!isTokenPending && (
                        <CurrencyExchangeConfirmation
                            data={confirmationData}
                            headerText={i18n.get("currencyExchange.confirm.summary.title")}
                            declarationContent={
                                isRetailEnvironment ? retailDeclarationContent : corporateDeclarationContent
                            }
                            declarationLinkText={i18n.get("currencyExchange.confirm.declarationLink")}
                            declarationDrawerTitle={i18n.get("currencyExchange.confirm.declarationTitle")}
                            downloadButtonText={i18n.get("global.download")}
                            onDownloadDeclaration={handleDownloadDeclaration}
                            loading={fetchingConfirm}
                            onConfirm={handleConfirm}
                            getToken={getToken}
                            getLabels={getLabel}
                            validationConfirm={otpSchema}
                        />
                    )}
                </Box>
            </Box>
        </>
    );
}

CurrencyExchangeConfirmPage.propTypes = {
    dispatch: func.isRequired,
    previewData: shape({}),
    fetchingConfirm: bool,
    accounts: arrayOf(shape({})),
    userId: string,
    tokenNumber: string,
    fetchingToken: bool,
    isDesktop: bool.isRequired,
    isRetailEnvironment: bool.isRequired,
    embeddedInV3Shell: bool,
};

CurrencyExchangeConfirmPage.defaultProps = {
    previewData: null,
    fetchingConfirm: false,
    accounts: [],
    userId: "",
    tokenNumber: null,
    fetchingToken: false,
    embeddedInV3Shell: false,
};

const mapStateToProps = (state) => ({
    previewData: currencyExchangeSelectors.getPreviewData(state),
    fetchingConfirm: currencyExchangeSelectors.getFetchingConfirm(state),
    accounts: accountsSelectors.getAccounts(state),
    userId: sessionSelectors.getUser(state)?.userId || "",
    tokenNumber: tokenSelectors.getTokenNumber(state),
    fetchingToken: tokenSelectors.getFetchingToken(state),
    isRetailEnvironment: sessionSelectors.isRetailEnvironment(state),
});

export default compose(withRouter, connect(mapStateToProps), resizableRoute)(CurrencyExchangeConfirmPage);
